<template>
  <section class="ncrm-data-collection-editor d-flex flex-column ga-4">
    <div>
      <div class="text-body-2 font-weight-semibold ncrm-text-strong">{{ $t(`${copyKey}.fieldsTitle`) }}</div>
      <div class="text-caption text-medium-emphasis mt-1">{{ $t(`${copyKey}.fieldsHint`) }}</div>
      <div class="ncrm-data-collection-editor__switches">
        <v-switch
          v-for="key in fieldKeys"
          :key="key"
          v-model="collectedFields[key]"
          color="primary"
          density="compact"
          hide-details
          :label="$t(`${copyKey}.fields.${key}`)"
        />
      </div>
      <v-alert v-if="!contactFields.length" type="error" density="compact" class="mt-2">
        {{ $t(`${copyKey}.contactRequired`) }}
      </v-alert>
    </div>

    <div>
      <div class="text-body-2 font-weight-semibold ncrm-text-strong">{{ $t(`${copyKey}.copyTitle`) }}</div>
      <div class="text-caption text-medium-emphasis mt-1">{{ $t(`${copyKey}.copyHint`) }}</div>
      <div class="ncrm-data-collection-editor__locales" role="group" :aria-label="$t(`${copyKey}.copyTitle`)">
        <v-btn
          v-for="locale in locales"
          :key="locale"
          :variant="activeLocale === locale ? 'tonal' : 'outlined'"
          :color="activeLocale === locale ? 'primary' : 'neutral'"
          :aria-pressed="activeLocale === locale"
          @click="selectLocale(locale)"
        >
          {{ locale.toUpperCase() }}
        </v-btn>
      </div>
      <div class="d-flex flex-column ga-3">
        <v-text-field
          v-model="localizedContent.title"
          :label="$t(`${copyKey}.title`)"
          maxlength="180"
          hide-details="auto"
        />
        <v-textarea
          v-model="localizedContent.description"
          :label="$t(`${copyKey}.description`)"
          maxlength="600"
          rows="3"
          hide-details="auto"
        />
      </div>
    </div>

    <template v-for="field in contactFields" :key="field">
      <v-divider />
      <NcrmWorkflowExternalFormPrivacy
        :form="form"
        :field="field"
        :select-menu-props="selectMenuProps"
        @catalog-refreshed="$emit('catalog-refreshed')"
      />
    </template>
  </section>
</template>

<script>
import {
  DATA_COLLECTION_LOCALES,
  DEFAULT_DATA_COLLECTION_FIELDS,
  dataCollectionFieldsDraft,
  dataCollectionLocalesDraft,
  enabledContactFields,
} from "@/Utile/ncrmExternalDataCollectionForm.mjs";
import NcrmWorkflowExternalFormPrivacy from "./NcrmWorkflowExternalFormPrivacy.vue";

// GDPR S5.3B (`GDPR-085`) — configurarea formularului „Colectare date”: câmpurile
// colectate, textul de sus per limbă și câte o mapare Privacy pe fiecare câmp de
// contact. Fiecare bifă din formularul public va afișa notice-ul mapat pe câmpul ei.
export default {
  name: "NcrmWorkflowDataCollectionEditor",
  components: { NcrmWorkflowExternalFormPrivacy },
  props: {
    form: { type: Object, required: true },
    selectMenuProps: { type: Object, default: () => ({}) },
  },
  emits: ["catalog-refreshed"],
  data: () => ({ activeLocale: "ro" }),
  computed: {
    copyKey() { return "ncrm.workflows.nodeSettings.externalForms.dataCollection"; },
    fieldKeys() { return Object.keys(DEFAULT_DATA_COLLECTION_FIELDS); },
    locales() { return DATA_COLLECTION_LOCALES; },
    // Aliasuri spre draftul din formular, ca switch-urile și textele să scrie direct în el.
    collectedFields() { return this.form.fields.data_collection; },
    contactFields() { return enabledContactFields(this.collectedFields); },
    localizedContent() { return this.form.content.dataCollectionLocales[this.activeLocale]; },
    // Limba editată e și limba preview-ului din CRM (ca la formularul cu fișiere).
    previewState() { return this.form; },
  },
  methods: {
    selectLocale(locale) {
      this.activeLocale = locale;
      this.previewState.dataCollectionPreviewLocale = locale;
    },
  },
  watch: {
    // Un formular nou sau schimbat din alt tip primește draftul complet, ca
    // switch-urile și câmpurile de text să scrie direct în el.
    form: {
      immediate: true,
      handler(form) {
        form.fields.data_collection = dataCollectionFieldsDraft(form.fields.data_collection);
        form.content.dataCollectionLocales = dataCollectionLocalesDraft(form.content.dataCollectionLocales);
        form.dataCollectionPreviewLocale = DATA_COLLECTION_LOCALES.includes(form.dataCollectionPreviewLocale) ? form.dataCollectionPreviewLocale : "ro";
        this.activeLocale = form.dataCollectionPreviewLocale;
      },
    },
  },
};
</script>

<style scoped>
.ncrm-data-collection-editor__switches { display: flex; flex-wrap: wrap; gap: 4px 24px; margin-top: 8px; }
.ncrm-data-collection-editor__locales { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
</style>
